import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Search, BadgeCheck, ShieldCheck, MapPin, Calendar, X, Loader2, Briefcase, Lock, LogIn, ExternalLink, Globe, ArrowLeft, ChevronDown, GraduationCap } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { profSummary } from "../content/professions";
import HofCompanySearch from "../components/HofCompanySearch";
import CandidatePreview from "../components/CandidatePreview";

const MENTOR = {
  sk: { label: "Mentor", title: "Môže pôsobiť ako mentor (školiteľ) programu „Pracuj, uč sa a zarábaj“." },
  en: { label: "Mentor", title: "Can act as a mentor (trainer) for the “Work, learn and earn” programme." },
  de: { label: "Mentor", title: "Kann als Mentor (Ausbilder) für das Programm „Arbeite, lerne und verdiene“ wirken." },
  pl: { label: "Mentor", title: "Może pełnić rolę mentora (szkoleniowca) programu „Pracuj, ucz się i zarabiaj“." },
  fr: { label: "Mentor", title: "Peut agir comme mentor (formateur) du programme « Travaille, apprends et gagne »." },
};
const NAB_LABELS = { sk: "Náborár", en: "Recruiter", de: "Personalvermittler", pl: "Rekruter", fr: "Recruteur" };
const RECRUITER_SKILL_IDS = ["naborar", "nab", "recruiter", "gen_naborar"];
const isMentor = (p) => p?.mentor === true || p?.is_mentor === true || (Array.isArray(p?.roles) && p.roles.includes("mentor"));

const MentorBadge = ({ lang, className = "" }) => (
  <span
    title={(MENTOR[lang] || MENTOR.sk).title}
    data-testid="hof-mentor-badge"
    className={`inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-[#D4AF37] text-black ${className}`}
  >
    <GraduationCap size={12} strokeWidth={2} /> {(MENTOR[lang] || MENTOR.sk).label}
  </span>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIGIK_PUBLIC = "https://tigik.saubereko.eu/api/hof/public";

const SECTORS = {
  sk: { industry: "Priemysel & Výroba", services: "Služby", prompt: "Vyberte oblasť podnikania — certifikované firmy sme rozdelili podľa sektora.", back: "Späť na výber" },
  en: { industry: "Industry & Manufacturing", services: "Services", prompt: "Choose a business area — we've grouped certified companies by sector.", back: "Back to selection" },
  de: { industry: "Industrie & Produktion", services: "Dienstleistungen", prompt: "Wählen Sie einen Geschäftsbereich — zertifizierte Unternehmen sind nach Sektor gegliedert.", back: "Zurück zur Auswahl" },
  pl: { industry: "Przemysł & Produkcja", services: "Usługi", prompt: "Wybierz obszar działalności — certyfikowane firmy podzieliliśmy według sektora.", back: "Powrót do wyboru" },
  fr: { industry: "Industrie & Production", services: "Services", prompt: "Choisissez un domaine d'activité — nous avons regroupé les entreprises certifiées par secteur.", back: "Retour à la sélection" },
};
const SECTOR_BADGE = {
  sk: { industry: "Priemysel", services: "Služby" },
  en: { industry: "Industry", services: "Services" },
  de: { industry: "Industrie", services: "Dienstleistungen" },
  pl: { industry: "Przemysł", services: "Usługi" },
  fr: { industry: "Industrie", services: "Services" },
};
const sectorBadge = (s, lang = "sk") => (SECTOR_BADGE[lang] || SECTOR_BADGE.sk)[s] || "";

const VERIFY = {
  sk: { title: "Overiť profesionála", ph: "Zadaj priezvisko…", prompt: "Zadaj priezvisko — ak osoba existuje, uvidíš verejnú vizitku.", none: "Nenašiel sa žiadny profesionál. Skontrolujte priezvisko.", hint: "Z dôvodu ochrany osobných údajov nezobrazujeme celý zoznam profesionálov — konkrétneho profesionála overíte cez vyhľadávanie.", skills: "Zručnosti" },
  en: { title: "Verify a professional", ph: "Enter surname…", prompt: "Enter a surname — if the person exists you'll see their public verification card.", none: "No professional found. Check the surname.", hint: "For privacy reasons we do not display the full list of professionals — verify a specific professional via search.", skills: "Skills" },
  de: { title: "Fachkraft überprüfen", ph: "Nachname eingeben…", prompt: "Gib den Nachnamen ein — falls die Person existiert, siehst du deren öffentliche Karte.", none: "Keine Fachkraft gefunden. Prüfen Sie den Nachnamen.", hint: "Aus Datenschutzgründen zeigen wir keine vollständige Liste — überprüfen Sie eine Fachkraft per Suche.", skills: "Fähigkeiten" },
  pl: { title: "Zweryfikuj profesjonalistę", ph: "Wpisz nazwisko…", prompt: "Wpisz nazwisko — jeśli osoba istnieje, zobaczysz publiczną wizytówkę.", none: "Nie znaleziono profesjonalisty. Sprawdź nazwisko.", hint: "Ze względu na ochronę danych nie wyświetlamy pełnej listy — zweryfikuj profesjonalistę przez wyszukiwanie.", skills: "Umiejętności" },
  fr: { title: "Vérifier un professionnel", ph: "Saisissez le nom de famille…", prompt: "Saisissez un nom de famille — si la personne existe, vous verrez sa carte publique.", none: "Aucun professionnel trouvé. Vérifiez le nom de famille.", hint: "Pour des raisons de protection des données, nous n'affichons pas la liste complète — vérifiez un professionnel via la recherche.", skills: "Compétences" },
};

const RECOG = {
  sk: { tab: "Uznania", intro: "Výnimočné osobnosti a firmy, ktorým SauberEko® udelilo uznanie za mimoriadny prínos.", empty: "Zatiaľ tu nie sú žiadne uznania." },
  en: { tab: "Recognitions", intro: "Exceptional individuals and companies recognised by SauberEko® for their outstanding contribution.", empty: "No recognitions have been awarded yet." },
  de: { tab: "Auszeichnungen", intro: "Herausragende Persönlichkeiten und Unternehmen, die von SauberEko® für ihren außergewöhnlichen Beitrag ausgezeichnet wurden.", empty: "Es wurden noch keine Auszeichnungen vergeben." },
  pl: { tab: "Wyróżnienia", intro: "Wyjątkowe osoby i firmy wyróżnione przez SauberEko® za wybitny wkład.", empty: "Nie przyznano jeszcze żadnych wyróżnień." },
  fr: { tab: "Distinctions", intro: "Des personnalités et des entreprises exceptionnelles distinguées par SauberEko® pour leur contribution remarquable.", empty: "Aucune distinction n'a encore été décernée." },
};

const I18N = {
  sk: {
    overline: "Verejný register",
    title: "Sieň Slávy SauberEko®",
    subtitle: "Oficiálny verejný register certifikovaných profesionálov Sa.Eko.Prs©. Overte si pravosť ktorejkoľvek certifikácie priamo tu.",
    search: "Hľadať podľa mena alebo čísla certifikátu…",
    count: (n) => `${n} certifikovaných profesionálov`,
    certLabel: "Certifikát",
    since: "Certifikovaný od",
    status: "Platný",
    verified: "Overený v registri",
    empty: "Nenašli sa žiadne záznamy.",
    loading: "Načítavam register…",
    error: "Register sa momentálne nepodarilo načítať. Skúste to prosím neskôr.",
    detail: "Detail záznamu",
    verifyNote: "Tento profesionál je oficiálne zapísaný vo verejnom registri SauberEko® a jeho certifikácia je platná.",
    close: "Zavrieť",
    publicCard: "Otvoriť verejnú vizitku",
    source: "Zdroj údajov: oficiálna databáza SauberEko® (tigik.saubereko.eu)",
    spec: "Špecializácia",
    company: "Firma",
    location: "Lokalita",
    website: "Web",
    tabPros: "Profesionáli",
    tabCompanies: "Firmy",
    countCompanies: (n) => `${n} certifikovaných firiem`,
    emptyCompanies: "Zatiaľ tu nie sú zapísané žiadne certifikované firmy.",
    tabRecruiters: "Preverení a absolventi",
    countRecruiters: (n) => `${n} preverených`,
    emptyRecruiters: "Zatiaľ tu nie sú zapísaní žiadni preverení.",
    lockedTitle: "Zoznam je len pre registrovaných",
    lockedText: "Na ochranu osobných údajov certifikovaných profesionálov je celý zoznam a detaily prístupné len prihláseným používateľom. Zaregistrujte sa alebo sa prihláste a získajte plný prístup k overeniu certifikácií.",
    lockedCta: "Prihlásiť sa / Zaregistrovať sa",
  },
  en: {
    overline: "Public register",
    title: "SauberEko® Hall of Fame",
    subtitle: "The official public register of certified Sa.Eko.Prs© professionals. Verify the authenticity of any certification right here.",
    search: "Search by name or certificate number…",
    count: (n) => `${n} certified professionals`,
    certLabel: "Certificate",
    since: "Certified since",
    status: "Valid",
    verified: "Verified in the register",
    empty: "No records found.",
    loading: "Loading the register…",
    error: "The register could not be loaded right now. Please try again later.",
    detail: "Record detail",
    verifyNote: "This professional is officially listed in the public SauberEko® register and their certification is valid.",
    close: "Close",
    publicCard: "Open public card",
    source: "Data source: official SauberEko® database (tigik.saubereko.eu)",
    spec: "Specialization",
    company: "Company",
    location: "Location",
    website: "Website",
    tabPros: "Professionals",
    tabCompanies: "Companies",
    countCompanies: (n) => `${n} certified companies`,
    emptyCompanies: "No certified companies are listed here yet.",
    tabRecruiters: "Verified & graduates",
    countRecruiters: (n) => `${n} verified`,
    emptyRecruiters: "No verified persons are listed here yet.",
    lockedTitle: "The list is for registered users only",
    lockedText: "To protect the personal data of certified professionals, the full list and details are accessible only to logged-in users. Register or sign in to gain full access to certification verification.",
    lockedCta: "Sign in / Register",
  },
  de: {
    overline: "Öffentliches Register",
    title: "SauberEko® Ruhmeshalle",
    subtitle: "Das offizielle öffentliche Register der zertifizierten Sa.Eko.Prs©-Fachleute. Überprüfen Sie die Echtheit jeder Zertifizierung direkt hier.",
    search: "Suche nach Name oder Zertifikatsnummer…",
    count: (n) => `${n} zertifizierte Fachleute`,
    certLabel: "Zertifikat",
    since: "Zertifiziert seit",
    status: "Gültig",
    verified: "Im Register verifiziert",
    empty: "Keine Einträge gefunden.",
    loading: "Register wird geladen…",
    error: "Das Register konnte derzeit nicht geladen werden. Bitte versuchen Sie es später erneut.",
    detail: "Eintragsdetail",
    verifyNote: "Dieser Fachmann ist offiziell im öffentlichen SauberEko®-Register eingetragen und seine Zertifizierung ist gültig.",
    close: "Schließen",
    publicCard: "Öffentliche Karte öffnen",
    source: "Datenquelle: offizielle SauberEko®-Datenbank (tigik.saubereko.eu)",
    spec: "Spezialisierung",
    company: "Unternehmen",
    location: "Standort",
    website: "Website",
    tabPros: "Fachleute",
    tabCompanies: "Unternehmen",
    countCompanies: (n) => `${n} zertifizierte Unternehmen`,
    emptyCompanies: "Hier sind noch keine zertifizierten Unternehmen eingetragen.",
    tabRecruiters: "Geprüfte & Absolventen",
    countRecruiters: (n) => `${n} Geprüfte`,
    emptyRecruiters: "Hier sind noch keine Geprüften eingetragen.",
    lockedTitle: "Die Liste ist nur für registrierte Nutzer",
    lockedText: "Zum Schutz der personenbezogenen Daten der zertifizierten Fachleute sind die vollständige Liste und die Details nur für angemeldete Nutzer zugänglich. Registrieren Sie sich oder melden Sie sich an, um vollen Zugang zur Zertifizierungsprüfung zu erhalten.",
    lockedCta: "Anmelden / Registrieren",
  },
  pl: {
    overline: "Rejestr publiczny",
    title: "Galeria Sław SauberEko®",
    subtitle: "Oficjalny publiczny rejestr certyfikowanych profesjonalistów Sa.Eko.Prs©. Zweryfikuj autentyczność certyfikacji bezpośrednio tutaj.",
    search: "Szukaj po nazwisku lub numerze certyfikatu…",
    count: (n) => `${n} certyfikowanych profesjonalistów`,
    certLabel: "Certyfikat",
    since: "Certyfikowany od",
    status: "Ważny",
    verified: "Zweryfikowany w rejestrze",
    empty: "Nie znaleziono wpisów.",
    loading: "Ładowanie rejestru…",
    error: "Nie udało się teraz załadować rejestru. Spróbuj ponownie później.",
    detail: "Szczegóły wpisu",
    verifyNote: "Ten profesjonalista jest oficjalnie wpisany do publicznego rejestru SauberEko®, a jego certyfikacja jest ważna.",
    close: "Zamknij",
    publicCard: "Otwórz publiczną wizytówkę",
    source: "Źródło danych: oficjalna baza SauberEko® (tigik.saubereko.eu)",
    spec: "Specjalizacja",
    company: "Firma",
    location: "Lokalizacja",
    website: "Strona",
    tabPros: "Profesjonaliści",
    tabCompanies: "Firmy",
    countCompanies: (n) => `${n} certyfikowanych firm`,
    emptyCompanies: "Nie wpisano tu jeszcze żadnych certyfikowanych firm.",
    tabRecruiters: "Zweryfikowani i absolwenci",
    countRecruiters: (n) => `${n} zweryfikowanych`,
    emptyRecruiters: "Nie wpisano tu jeszcze żadnych zweryfikowanych osób.",
    lockedTitle: "Lista jest tylko dla zarejestrowanych",
    lockedText: "W celu ochrony danych osobowych certyfikowanych profesjonalistów pełna lista i szczegóły są dostępne wyłącznie dla zalogowanych użytkowników. Zarejestruj się lub zaloguj, aby uzyskać pełny dostęp do weryfikacji certyfikacji.",
    lockedCta: "Zaloguj się / Zarejestruj się",
  },
  fr: {
    overline: "Registre public",
    title: "Panthéon SauberEko®",
    subtitle: "Le registre public officiel des professionnels certifiés Sa.Eko.Prs©. Vérifiez l'authenticité de toute certification directement ici.",
    search: "Rechercher par nom ou numéro de certificat…",
    count: (n) => `${n} professionnels certifiés`,
    certLabel: "Certificat",
    since: "Certifié depuis",
    status: "Valide",
    verified: "Vérifié au registre",
    empty: "Aucun enregistrement trouvé.",
    loading: "Chargement du registre…",
    error: "Le registre n'a pas pu être chargé pour le moment. Veuillez réessayer plus tard.",
    detail: "Détail de l'enregistrement",
    verifyNote: "Ce professionnel est officiellement inscrit au registre public SauberEko® et sa certification est valide.",
    close: "Fermer",
    publicCard: "Ouvrir la carte publique",
    source: "Source des données : base de données officielle SauberEko® (tigik.saubereko.eu)",
    spec: "Spécialisation",
    company: "Société",
    location: "Localité",
    website: "Site web",
    tabPros: "Professionnels",
    tabCompanies: "Entreprises",
    countCompanies: (n) => `${n} entreprises certifiées`,
    emptyCompanies: "Aucune entreprise certifiée n'est encore inscrite ici.",
    tabRecruiters: "Vérifiés & diplômés",
    countRecruiters: (n) => `${n} vérifiés`,
    emptyRecruiters: "Aucune personne vérifiée n'est encore inscrite ici.",
    lockedTitle: "La liste est réservée aux utilisateurs inscrits",
    lockedText: "Afin de protéger les données personnelles des professionnels certifiés, la liste complète et les détails sont accessibles uniquement aux utilisateurs connectés. Inscrivez-vous ou connectez-vous pour obtenir un accès complet à la vérification des certifications.",
    lockedCta: "Se connecter / S'inscrire",
  },
};

const displayName = (p) => (p.cert_holder_name || p.name || "").trim() || "—";
const getWebsite = (p) => (p.website || p.web || p.url || "").trim();
const normUrl = (u) => (/^https?:\/\//i.test(u) ? u : "https://" + u);
const prettyUrl = (u) => u.replace(/^https?:\/\//i, "").replace(/\/$/, "");

const fmtDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(
      { sk: "sk-SK", en: "en-GB", de: "de-DE", pl: "pl-PL", fr: "fr-FR" }[lang] || "sk-SK",
      { day: "2-digit", month: "long", year: "numeric" }
    );
  } catch {
    return iso.slice(0, 10);
  }
};

const VERIFY_R = {
  sk: { title: "Overiť preverenú osobu alebo absolventa", ph: "Zadaj priezvisko…", prompt: "Zadaj priezvisko — ak osoba existuje, uvidíš jej verejnú vizitku.", none: "Nenašla sa žiadna preverená osoba ani absolvent. Skontrolujte priezvisko.", hint: "Z dôvodu ochrany osobných údajov nezobrazujeme celý zoznam — konkrétnu osobu overíte cez vyhľadávanie.", skills: "Zručnosti", grad: "Absolvent", ver: "Preverený" },
  en: { title: "Verify a verified person or graduate", ph: "Enter surname…", prompt: "Enter a surname — if the person exists you'll see their public card.", none: "No verified person or graduate found. Check the surname.", hint: "For privacy reasons we do not display the full list — verify a specific person via search.", skills: "Skills", grad: "Graduate", ver: "Verified" },
  de: { title: "Geprüfte Person oder Absolvent überprüfen", ph: "Nachname eingeben…", prompt: "Gib den Nachnamen ein — falls die Person existiert, siehst du ihre öffentliche Karte.", none: "Keine geprüfte Person und kein Absolvent gefunden. Prüfen Sie den Nachnamen.", hint: "Aus Datenschutzgründen zeigen wir keine vollständige Liste — überprüfen Sie eine Person per Suche.", skills: "Fähigkeiten", grad: "Absolvent", ver: "Geprüft" },
  pl: { title: "Zweryfikuj osobę zweryfikowaną lub absolwenta", ph: "Wpisz nazwisko…", prompt: "Wpisz nazwisko — jeśli osoba istnieje, zobaczysz jej publiczną wizytówkę.", none: "Nie znaleziono osoby zweryfikowanej ani absolwenta. Sprawdź nazwisko.", hint: "Ze względu na ochronę danych nie wyświetlamy pełnej listy — zweryfikuj osobę przez wyszukiwanie.", skills: "Umiejętności", grad: "Absolwent", ver: "Zweryfikowany" },
  fr: { title: "Vérifier une personne vérifiée ou un diplômé", ph: "Saisissez le nom de famille…", prompt: "Saisissez un nom de famille — si la personne existe, vous verrez sa carte publique.", none: "Aucune personne vérifiée ni aucun diplômé trouvé. Vérifiez le nom de famille.", hint: "Pour des raisons de protection des données, nous n'affichons pas la liste complète — vérifiez une personne via la recherche.", skills: "Compétences", grad: "Diplômé", ver: "Vérifié" },
};

const normCert = (c) => String(c || "").replace(/\s+/g, "").toLowerCase();
const WLE_T = { sk: "Pečať Work, learn and earn©", en: "Work, learn and earn© seal", de: "Siegel Arbeite, lerne und verdiene©", pl: "Pieczęć Work, learn and earn©", fr: "Sceau Work, learn and earn©" };
const WLE_D = {
  sk: "Táto firma je partnerom programu Work, learn and earn© — pravé miesto pre mladých záujemcov, ktorí sa chcú zdokonaľovať.",
  en: "This company is a partner of the Work, learn and earn© program — the right place for young candidates who want to develop.",
  de: "Dieses Unternehmen ist Partner des Programms Arbeite, lerne und verdiene© — der richtige Ort für junge Interessenten, die sich weiterentwickeln wollen.",
  pl: "Ta firma jest partnerem programu Work, learn and earn© — właściwe miejsce dla młodych kandydatów, którzy chcą się doskonalić.",
  fr: "Cette entreprise est partenaire du programme Work, learn and earn© — le bon endroit pour les jeunes candidats qui veulent se perfectionner.",
};

const Initials = ({ name }) => {
  const letters = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black font-display font-semibold text-lg shadow-[0_6px_20px_rgba(212,175,55,0.35)]">
      {letters || "•"}
    </div>
  );
};

export default function HallOfFamePage() {
  const { lang } = useLang();
  const tr = I18N[lang] || I18N.sk;
  const [state, setState] = useState({ loading: true, error: false, pros: [], companies: [], recruiters: [] });
  const [counts, setCounts] = useState({ professionals_count: 0, companies_count: 0, recruiters_count: 0 });
  const [tab, setTab] = useState("pros");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [wle, setWle] = useState(new Set());
  const [badges, setBadges] = useState({});
  const [profs, setProfs] = useState({});
  useEffect(() => {
    fetch(`${API}/hof/badges`).then((r) => r.json()).then((d) => { setBadges(d.badges || {}); setProfs(d.professions || {}); }).catch(() => {});
    fetch(`${API}/hof/wle-partners`).then((r) => r.json()).then((d) => setWle(new Set((d.certs || []).map(normCert)))).catch(() => {});
  }, []);
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [sector, setSector] = useState(null); // null = not picked yet (step 1)
  const [pubCompanies, setPubCompanies] = useState({ loading: true, error: false, list: [] });
  const [verify, setVerify] = useState({ q: "", loading: false, results: [], searched: false });
  const [catalog, setCatalog] = useState(null);
  const [recognitions, setRecognitions] = useState({ loading: true, error: false, list: [] });
  const [expandedSkills, setExpandedSkills] = useState({});

  // Skills catalog (single source of truth from Tigik) — fetch once
  useEffect(() => {
    fetch(`${TIGIK_PUBLIC.replace("/public", "/skills-catalog")}`)
      .then((r) => r.json())
      .then((d) => setCatalog(d))
      .catch(() => {});
  }, []);

  const skillLabels = useMemo(() => {
    const map = {};
    (catalog?.groups || []).forEach((g) => (g.skills || []).forEach((s) => {
      map[s.id] = (s.labels && (s.labels[lang] || s.labels.sk)) || s.id;
    }));
    RECRUITER_SKILL_IDS.forEach((id) => { if (!map[id]) map[id] = NAB_LABELS[lang] || NAB_LABELS.sk; });
    return map;
  }, [catalog, lang]);

  // Public professional verification (GDPR-friendly search, no full listing)
  useEffect(() => {
    if (tab !== "pros" && tab !== "recruiters") return;
    const q = verify.q.trim();
    if (q.length < 3) { setVerify((v) => ({ ...v, results: [], searched: false, loading: false })); return; }
    let alive = true;
    setVerify((v) => ({ ...v, loading: true }));
    const timer = setTimeout(() => {
      const url = tab === "recruiters"
        ? `${API}/hof/recruiters/verify?q=${encodeURIComponent(q)}`
        : `${API}/hof/verify-pro?q=${encodeURIComponent(q)}`;
      fetch(url)
        .then((r) => r.json())
        .then((d) => { if (alive) setVerify((v) => ({ ...v, loading: false, searched: true, results: d.results || [] })); })
        .catch(() => { if (alive) setVerify((v) => ({ ...v, loading: false, searched: true, results: [] })); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); };
  }, [verify.q, tab]);

  // Public certified companies (GDPR-safe, no auth) — fetch ALL once, filter client-side
  useEffect(() => {
    let alive = true;
    setPubCompanies((s) => ({ ...s, loading: true, error: false }));
    fetch(`${API}/hof/companies-public`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then((d) => {
        if (!alive) return;
        setPubCompanies({ loading: false, error: false, list: d.companies || [] });
      })
      .catch(() => { if (alive) setPubCompanies((s) => ({ ...s, loading: false, error: true })); });
    return () => { alive = false; };
  }, []);

  // Public recognitions ("Uznania") from Tigik — no auth, GDPR-safe consented publication
  useEffect(() => {
    let alive = true;
    fetch(`${TIGIK_PUBLIC.replace("/public", "/recognitions")}?include_photos=0`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then((d) => { if (alive) setRecognitions({ loading: false, error: false, list: Array.isArray(d) ? d : (d.recognitions || d.items || []) }); })
      .catch(() => { if (alive) setRecognitions((s) => ({ ...s, loading: false, error: true })); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${tr.title} — SauberEko®`;
  }, [tr.title]);

  // Public counts (SEO-safe, no personal data). Full list only via token-protected backend proxy.
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: false }));

    fetch(`${API}/hof/counts`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then((c) => { if (alive) setCounts(c); })
      .catch(() => { if (alive) setState((s) => ({ ...s, error: true })); });

    const token = localStorage.getItem("saubereko_applicant_token") || localStorage.getItem("saubereko_admin_token");
    if (!token) {
      if (alive) { setState((s) => ({ ...s, loading: false })); setAuthed(false); }
      return () => { alive = false; };
    }
    fetch(`${API}/hof/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.status === 401) { if (alive) setAuthed(false); return null; }
        if (!r.ok) throw new Error("bad");
        return r.json();
      })
      .then((d) => {
        if (!alive) return;
        if (d) {
          setState((s) => ({
            ...s, loading: false, error: false,
            pros: d.professionals || [], companies: d.companies || [], recruiters: d.recruiters || [],
          }));
          setAuthed(true);
        } else {
          setState((s) => ({ ...s, loading: false }));
        }
      })
      .catch(() => { if (alive) { setState((s) => ({ ...s, loading: false })); setAuthed(false); } });
    return () => { alive = false; };
  }, []);

  const isCompanies = tab === "companies";
  const isWle = (p) => isCompanies && wle.has(normCert(p?.certificate_no));
  const isPros = tab === "pros" || tab === "recruiters";
  const V = tab === "recruiters" ? VERIFY_R[lang] || VERIFY_R.sk : VERIFY[lang] || VERIFY.sk;
  const isRecog = tab === "recognitions";
  const companiesForSector = isCompanies && sector ? pubCompanies.list.filter((c) => c.sector === sector) : [];
  const source = tab === "pros" ? state.pros : isCompanies ? companiesForSector : state.recruiters;
  const countFn = tab === "pros" ? tr.count : isCompanies ? tr.countCompanies : tr.countRecruiters;
  const emptyMsg = isCompanies ? tr.emptyCompanies : tab === "recruiters" ? tr.emptyRecruiters : tr.empty;
  const pubCount = tab === "pros" ? counts.professionals_count : isCompanies ? companiesForSector.length : counts.recruiters_count;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return source;
    return source.filter((p) =>
      displayName(p).toLowerCase().includes(q) ||
      (p.certificate_no || "").toLowerCase().includes(q)
    );
  }, [query, source]);

  return (
    <div data-testid="hall-of-fame-page" className="min-h-screen bg-[#070707] pt-28 pb-24 relative overflow-hidden">
      <div className="absolute left-1/2 top-32 -translate-x-1/2 w-[700px] h-[500px] bg-[#D4AF37]/8 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6">
        <Reveal>
          <div className="text-center">
            <div className="flex justify-center mb-7">
              <div className="relative">
                <div className="absolute inset-0 bg-[#D4AF37]/30 blur-2xl rounded-full" />
                <div className="relative w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black shadow-[0_10px_40px_rgba(212,175,55,0.4)] ring-1 ring-[#F2E3C6]/50">
                  <Trophy size={34} strokeWidth={1.75} />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-5">
              <span className="h-px w-8 bg-[#D4AF37]/60" />
              {tr.overline}
              <span className="h-px w-8 bg-[#D4AF37]/60" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight gold-gradient-text leading-tight">
              {tr.title}
            </h1>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
              {tr.subtitle}
            </p>
          </div>
        </Reveal>

        {/* Tabs */}
        {!state.loading && !state.error && (
          <div data-testid="hof-tabs" className="flex items-center justify-center gap-2 mt-9">
            {[
              { key: "pros", label: tr.tabPros, n: counts.professionals_count },
              { key: "companies", label: tr.tabCompanies, n: counts.companies_count },
              { key: "recruiters", label: tr.tabRecruiters, n: counts.recruiters_count },
              { key: "recognitions", label: RECOG[lang].tab, n: recognitions.list.length },
            ].map((tb) => (
              <button
                key={tb.key}
                data-testid={`hof-tab-${tb.key}`}
                onClick={() => { setTab(tb.key); setQuery(""); setSelected(null); setSector(null); setVerify({ q: "", loading: false, results: [], searched: false }); }}
                className={`px-5 py-2 text-sm tracking-wide border transition-colors duration-300 ${
                  tab === tb.key
                    ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold"
                    : "text-white/70 border-white/15 hover:border-[#D4AF37]/50 hover:text-white"
                }`}
              >
                {tb.label} <span className="opacity-70">({tb.n})</span>
              </button>
            ))}
          </div>
        )}

        {/* STEP 1: Sector picker — companies only (public, 2-step UX) */}
        {isCompanies && !pubCompanies.error && (
          <HofCompanySearch lang={lang} companies={pubCompanies.list} loading={pubCompanies.loading} onSelect={setSelected} />
        )}

        {/* Professionals — verify-only search (GDPR: no full listing) */}
        {isPros && (
          <div className="mt-8" data-testid="hof-verify">
            <h2 className="text-center font-display text-2xl text-white mb-2">{V.title}</h2>
            <p className="text-center text-white/45 text-sm mb-6 max-w-xl mx-auto leading-relaxed">{V.hint}</p>
            <div className="max-w-xl mx-auto relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="hof-verify-input"
                type="text"
                value={verify.q}
                onChange={(e) => setVerify((v) => ({ ...v, q: e.target.value }))}
                placeholder={V.ph}
                className="w-full bg-[#0d0d0c] border border-[#D4AF37]/25 focus:border-[#D4AF37] outline-none text-white placeholder:text-white/35 pl-12 pr-11 py-3.5 transition-colors"
              />
              {verify.loading && <Loader2 size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#D4AF37] animate-spin" />}
            </div>

            {verify.q.trim().length < 3 && (
              <p data-testid="hof-verify-prompt" className="text-center text-white/40 text-sm mt-8">{V.prompt}</p>
            )}
            {verify.q.trim().length >= 3 && verify.searched && verify.results.length === 0 && !verify.loading && (
              <p data-testid="hof-verify-none" className="text-center text-white/40 text-sm mt-8">{V.none}</p>
            )}
            {verify.results.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 mt-8 max-w-3xl mx-auto">
                {verify.results.map((p, i) => {
                  const name = displayName(p);
                  return (
                    <button
                      key={p.certificate_no || i}
                      data-testid={`hof-verify-result-${i}`}
                      onClick={() => setSelected(p)}
                      className="group text-left flex items-center gap-4 bg-gradient-to-b from-[#0d0d0c] to-[#080808] border border-white/10 hover:border-[#D4AF37]/50 p-4 transition-all duration-300 hover:shadow-[0_10px_40px_rgba(212,175,55,0.12)]"
                    >
                      <Initials name={name} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-display text-lg text-white truncate group-hover:text-[#F2E3C6] transition-colors">{name}</p>
                          {isMentor(p) && <MentorBadge lang={lang} className="shrink-0" />}
                        </div>
                        <p className="text-[#D4AF37] text-xs tracking-wide font-mono mt-0.5">{p.certificate_no}</p>
                        {profs[normCert(p.certificate_no)] && (
                          <p data-testid={`hof-professions-${normCert(p.certificate_no)}`} className="text-[#F2E3C6]/80 text-xs mt-1 leading-snug">{profSummary(profs[normCert(p.certificate_no)], lang)}</p>
                        )}
                        <div className="flex items-center gap-1.5 mt-1.5 text-white/40 text-xs">
                          <BadgeCheck size={13} className="text-[#D4AF37]" />
                          {tab === "recruiters" ? (p.graduate ? V.grad : V.ver) : tr.verified}
                          {tab === "recruiters" && p.specialization ? <span className="text-white/35"> · {p.specialization}</span> : null}
                        </div>
                        {(p.skills?.length > 0 || p.skills_other) && (() => {
                          const total = (p.skills?.length || 0) + (p.skills_other ? 1 : 0);
                          const key = p.certificate_no || i;
                          const open = !!expandedSkills[key];
                          return (
                            <div className="mt-2.5" data-testid={`hof-verify-skills-${i}`}>
                              <span
                                role="button"
                                tabIndex={0}
                                data-testid={`hof-verify-skills-toggle-${i}`}
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setExpandedSkills((s) => ({ ...s, [key]: !s[key] })); }}
                                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); setExpandedSkills((s) => ({ ...s, [key]: !s[key] })); } }}
                                className="inline-flex items-center gap-1.5 text-[11px] tracking-wide px-2.5 py-1 rounded-full bg-[#D4AF37]/12 text-[#F2E3C6] border border-[#D4AF37]/25 hover:bg-[#D4AF37]/20 transition-colors cursor-pointer"
                              >
                                {V.skills} ({total})
                                <ChevronDown size={12} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                              </span>
                              {open && (
                                <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`hof-verify-skills-list-${i}`}>
                                  {(p.skills || []).map((id) => (
                                    <span key={id} className="text-[10px] tracking-wide px-2 py-0.5 rounded-full bg-[#D4AF37]/12 text-[#F2E3C6] border border-[#D4AF37]/25">
                                      {skillLabels[id] || id}
                                    </span>
                                  ))}
                                  {p.skills_other && (
                                    <span className="text-[10px] tracking-wide px-2 py-0.5 rounded-full bg-white/5 text-white/60 border border-white/15">
                                      {p.skills_other}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      <ShieldCheck size={18} className="text-white/20 group-hover:text-[#D4AF37] transition-colors shrink-0" />
                    </button>
                  );
                })}
              </div>
            )}
            <CandidatePreview lang={lang} testid="hof-advanced-search" title={({ sk: "Rozšírené vyhľadávanie – voľní profesionáli podľa zručností", en: "Advanced search – available professionals by skills", de: "Erweiterte Suche – verfügbare Fachkräfte nach Fähigkeiten", pl: "Wyszukiwanie zaawansowane – dostępni fachowcy według umiejętności", fr: "Recherche avancée – professionnels disponibles par compétences" })[lang]} />
          </div>
        )}

        {/* Recognitions ("Uznania") — public, no auth */}
        {isRecog && (
          <div className="mt-10" data-testid="hof-recognitions">
            <p className="text-center text-white/50 text-sm mb-8 max-w-xl mx-auto leading-relaxed">{RECOG[lang].intro}</p>
            {recognitions.loading ? (
              <div className="flex items-center justify-center gap-3 text-white/50 py-12"><Loader2 size={20} className="animate-spin text-[#D4AF37]" /></div>
            ) : recognitions.list.length === 0 ? (
              <p data-testid="hof-recognitions-empty" className="text-center text-white/40 py-12">{RECOG[lang].empty}</p>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 max-w-4xl mx-auto">
                {recognitions.list.map((r, i) => (<RecognitionCard key={r.id || i} r={r} idx={i} />))}
              </div>
            )}
          </div>
        )}

        {/* Search — logged-in users; companies only after a sector is picked */}
        {(((authed === true || tab === "recruiters") && !isCompanies && !isPros && !isRecog) || (isCompanies && sector)) && (source.length > 0 || query.trim()) && (
          <div className="mt-6 max-w-xl mx-auto relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="hof-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tr.search}
              className="w-full bg-[#0d0d0c] border border-[#D4AF37]/25 focus:border-[#D4AF37] outline-none text-white placeholder:text-white/35 pl-12 pr-4 py-3.5 transition-colors"
            />
          </div>
        )}

        {/* Count — public; for companies only after a sector is picked; not for verify-only pros */}
        {!isPros && !isRecog && !(isCompanies ? pubCompanies.loading : state.loading) && !(isCompanies ? pubCompanies.error : state.error) && !(isCompanies && !sector) && (
          <p data-testid="hof-count" className="text-center text-white/45 text-sm mt-6">
            {countFn(((authed === true || tab === "recruiters") || isCompanies) ? filtered.length : pubCount)}
          </p>
        )}

        {/* States */}
        {!isRecog && (isCompanies ? pubCompanies.loading : state.loading) && (
          <div data-testid="hof-loading" className="flex items-center justify-center gap-3 text-white/50 mt-16">
            <Loader2 size={20} className="animate-spin text-[#D4AF37]" /> {tr.loading}
          </div>
        )}

        {!isRecog && (isCompanies ? pubCompanies.error : state.error) && (
          <p data-testid="hof-error" className="text-center text-white/50 mt-16">{tr.error}</p>
        )}

        {/* Locked panel — visitors who are not logged in (not for public companies or verify-only pros) */}
        {!isCompanies && !isPros && !isRecog && !state.loading && !state.error && authed === false && tab !== "recruiters" && (
          <div data-testid="hof-locked" className="max-w-xl mx-auto mt-12 text-center bg-gradient-to-b from-[#0d0d0c] to-[#080808] border border-[#D4AF37]/25 px-8 py-12">
            <div className="flex justify-center mb-5">
              <div className="w-14 h-14 rounded-full flex items-center justify-center bg-[#D4AF37]/12 ring-1 ring-[#D4AF37]/30">
                <Lock size={26} className="text-[#D4AF37]" />
              </div>
            </div>
            <h2 className="font-display text-2xl text-white">{tr.lockedTitle}</h2>
            <p className="text-white/55 text-sm leading-relaxed mt-4">{tr.lockedText}</p>
            <Link
              to="/ziadost"
              data-testid="hof-locked-cta"
              className="group inline-flex items-center justify-center gap-2 mt-7 bg-[#D4AF37] text-black font-semibold px-8 py-3.5 hover:bg-[#F2E3C6] transition-colors duration-300"
            >
              <LogIn size={18} /> {tr.lockedCta}
            </Link>
          </div>
        )}

        {/* Empty */}
        {((isCompanies && sector && !pubCompanies.loading && !pubCompanies.error) || (!isCompanies && !isPros && !isRecog && (authed === true || tab === "recruiters") && !state.loading && !state.error)) && filtered.length === 0 && (
          <p data-testid="hof-empty" className="text-center text-white/40 mt-16">
            {query.trim() ? tr.empty : emptyMsg}
          </p>
        )}

        {/* List — logged-in users (recruiters) + public companies (after sector picked) */}
        {(((authed === true || tab === "recruiters") && !isCompanies && !isPros && !isRecog) || (isCompanies && sector)) && (
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {filtered.map((p, i) => {
              const name = displayName(p);
              const badge = isCompanies ? sectorBadge(p.sector, lang) : "";
              return (
                <button
                  key={p.certificate_no || i}
                  data-testid={`hof-card-${i}`}
                  onClick={() => setSelected(p)}
                  className="group text-left flex items-center gap-4 bg-gradient-to-b from-[#0d0d0c] to-[#080808] border border-white/10 hover:border-[#D4AF37]/50 p-4 transition-all duration-300 hover:shadow-[0_10px_40px_rgba(212,175,55,0.12)]"
                >
                  {isWle(p) ? (
                    <span className="seal-shine relative w-12 h-12 shrink-0 rounded-full overflow-hidden ring-1 ring-[#D4AF37]/50 shadow-[0_4px_18px_rgba(212,175,55,0.3)]" data-testid={`hof-card-${i}-wle-seal`}>
                      <img src="/wle-partner-seal.webp?v=2" alt={WLE_T[lang] || WLE_T.sk} className="w-full h-full object-cover" />
                    </span>
                  ) : <Initials name={name} />}
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-lg text-white truncate group-hover:text-[#F2E3C6] transition-colors">{name}</p>
                    <p className="text-[#D4AF37] text-xs tracking-wide font-mono mt-0.5">{p.certificate_no}</p>
                        {profs[normCert(p.certificate_no)] && (
                          <p data-testid={`hof-professions-${normCert(p.certificate_no)}`} className="text-[#F2E3C6]/80 text-xs mt-1 leading-snug">{profSummary(profs[normCert(p.certificate_no)], lang)}</p>
                        )}
                    {isCompanies && (p.city || p.country) && (
                      <p className="text-white/45 text-xs mt-0.5">{[p.city, p.country].filter(Boolean).join(", ")}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="flex items-center gap-1.5 text-white/40 text-xs">
                        <BadgeCheck size={13} className="text-[#D4AF37]" />
                        {tr.verified}
                      </span>
                      {isWle(p) && (
                        <span data-testid={`hof-card-${i}-wle`} className="text-[10px] tracking-wide px-2 py-0.5 rounded-full bg-gradient-to-r from-[#D4AF37]/30 to-[#F2E3C6]/15 text-[#F2E3C6] border border-[#D4AF37]/60 font-semibold">Work, learn and earn©</span>
                      )}
                      {badge && (
                        <span data-testid={`hof-card-${i}-sector`} className="text-[10px] tracking-wide px-2 py-0.5 rounded-full bg-[#D4AF37]/12 text-[#F2E3C6] border border-[#D4AF37]/25">{badge}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {getWebsite(p) && <Globe size={16} className="text-[#D4AF37]/80" />}
                    <ShieldCheck size={18} className="text-white/20 group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <p className="text-center text-white/25 text-xs mt-12">{tr.source}</p>
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          data-testid="hof-detail-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelected(null)}
        >
          <div
            className="relative w-full max-w-md bg-gradient-to-b from-[#111110] to-[#070707] border border-[#D4AF37]/30 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-1 w-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
            <button
              data-testid="hof-modal-close"
              onClick={() => setSelected(null)}
              className="absolute right-3 top-3 text-white/50 hover:text-white transition-colors"
              aria-label={tr.close}
            >
              <X size={22} />
            </button>
            <div className="px-7 py-8">
              <div className="flex items-center gap-4">
                <Initials name={displayName(selected)} />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-2xl text-white">{displayName(selected)}</p>
                    {isMentor(selected) && <MentorBadge lang={lang} />}
                  </div>
                  <p className="text-[#D4AF37] text-sm font-mono">{selected.certificate_no}</p>
                </div>
              </div>

              {isWle(selected) && (
                <div data-testid="hof-modal-wle" className="mt-6 flex items-center gap-4 border border-[#D4AF37]/40 bg-gradient-to-br from-[#16130a] to-[#0a0a09] p-4">
                  <span className="seal-shine relative w-20 h-20 shrink-0 rounded-full overflow-hidden ring-1 ring-[#D4AF37]/50 shadow-[0_6px_24px_rgba(212,175,55,0.3)]">
                    <img src="/wle-partner-seal.webp?v=2" alt={WLE_T[lang] || WLE_T.sk} className="w-full h-full object-cover" />
                  </span>
                  <div>
                    <p className="font-display text-base gold-gradient-text">{WLE_T[lang] || WLE_T.sk}</p>
                    <p className="text-white/60 text-xs leading-relaxed mt-1">{WLE_D[lang] || WLE_D.sk}</p>
                  </div>
                </div>
              )}

              <div className="mt-6 space-y-3 text-sm">
                <Row icon={Calendar} label={tr.since} value={fmtDate(selected.certified_since, lang)} />
                {selected.specialization && <Row icon={Briefcase} label={tr.spec} value={selected.specialization} />}
                {selected.company && <Row icon={Briefcase} label={tr.company} value={selected.company} />}
                {(selected.city || selected.country) && (
                  <Row icon={MapPin} label={tr.location} value={[selected.city, selected.country].filter(Boolean).join(", ")} />
                )}
                {getWebsite(selected) && (
                  <div className="flex items-center gap-3">
                    <Globe size={16} className="text-[#D4AF37]/70 shrink-0" />
                    <span className="text-white/45 w-32 shrink-0">{tr.website}</span>
                    <a
                      href={normUrl(getWebsite(selected))}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="hof-modal-website"
                      className="text-[#D4AF37] hover:text-[#F2E3C6] underline underline-offset-2 truncate"
                    >
                      {prettyUrl(getWebsite(selected))}
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-7 flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/25 px-4 py-3">
                <BadgeCheck size={22} className="text-[#D4AF37] shrink-0" />
                <div>
                  <p className="text-[#F2E3C6] text-sm font-semibold" data-testid="hof-modal-status-title">{badges[normCert(selected.certificate_no)]?.i18n?.[lang]?.title || badges[normCert(selected.certificate_no)]?.title || tr.status}</p>
                  <p className="text-white/55 text-xs leading-relaxed mt-0.5" data-testid="hof-modal-status-text">{badges[normCert(selected.certificate_no)]?.i18n?.[lang]?.text || badges[normCert(selected.certificate_no)]?.text || tr.verifyNote}</p>
                  {profs[normCert(selected.certificate_no)] && (
                    <p className="text-[#F2E3C6] text-xs font-semibold mt-1.5" data-testid="hof-modal-professions">{profSummary(profs[normCert(selected.certificate_no)], lang)}</p>
                  )}
                </div>
              </div>

              {selected.certificate_no && (
                <Link
                  to={`/certifikat/${encodeURIComponent(selected.certificate_no)}`}
                  data-testid="hof-modal-public-card"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-semibold px-6 py-3 transition-colors duration-300"
                >
                  <ExternalLink size={17} /> {tr.publicCard}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <Icon size={16} className="text-[#D4AF37]/70 shrink-0" />
    <span className="text-white/45 w-32 shrink-0">{label}</span>
    <span className="text-white/85">{value}</span>
  </div>
);


const RecognitionCard = ({ r, idx }) => {
  const loc = [r.city, r.country].filter(Boolean).join(", ");
  const date = (r.awarded_date || "").slice(0, 10);
  return (
    <div
      data-testid={`hof-recognition-${idx}`}
      className="flex flex-col bg-gradient-to-b from-[#0d0d0c] to-[#080808] border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 p-6 transition-all duration-300 hover:shadow-[0_10px_40px_rgba(212,175,55,0.12)]"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-b from-[#F2E3C6] to-[#D4AF37] text-black shrink-0 ring-1 ring-[#F2E3C6]/50">
          <Trophy size={22} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-lg text-[#F2E3C6] leading-snug">{r.recognition_title}</p>
          <p className="text-white text-sm font-medium mt-0.5">{r.name}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-white/45">
            {r.category && <span className="px-2 py-0.5 rounded-full bg-[#D4AF37]/12 text-[#D4AF37] border border-[#D4AF37]/25">{r.category}</span>}
            {loc && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {loc}</span>}
            {date && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {date}</span>}
          </div>
        </div>
      </div>
      {r.description && (
        <p className="text-white/60 text-sm leading-relaxed mt-4 whitespace-pre-line border-t border-white/5 pt-4">{r.description}</p>
      )}
    </div>
  );
};
